'use strict'
import type { Plugin } from 'vite'
import type { VersionInfo } from '../types.ts'
import { VersionInfoService } from '../services/VersionInfoService.ts'

/**
 * 默认日志管理器
 */
class Logger {
  private readonly needPrint: boolean
  constructor(needPrint: boolean) {
    this.needPrint = needPrint
  }
  info(message: unknown) {
    if (this.needPrint) console.log(message)
  }
  warn(message: unknown) {
    if (this.needPrint) console.warn(message)
  }
  error(message: unknown) {
    if (this.needPrint) console.error(message)
  }
}

/**
 * 版本信息插件配置选项
 */
export interface VersionPluginOptions {
  /** 是否包含 Git 信息，默认为 true */
  includeGitInfo?: boolean
  /** 是否包含构建时间，默认为 true */
  includeBuildTime?: boolean
  /** 时间格式化函数 */
  timeFormatter?: (date: Date) => string
}

/**
 * 版本信息插件
 */
export function versionInfoPlugin(options: VersionPluginOptions = {}): Plugin {
  const virtualModuleId = 'virtual:version-info'
  // 在开发环境中默认启用日志，在生产环境中默认禁用
  const logger = new Logger(process.env['NODE_ENV'] === 'development')

  let versionInfo: Partial<VersionInfo> = {}
  const resolvedVirtualModuleId = `\0${virtualModuleId}`

  return {
    name: 'version-info',

    async configResolved(config) {
      // 生成版本信息
      try {
        // 从 package.json 获取基本信息
        let packageInfo: Record<string, unknown> = {}
        try {
          packageInfo = await VersionInfoService.getPackageInfo(config.root)
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error'
          logger.warn(`⚠️ ${errorMessage}`)
        }

        versionInfo = {
          // ...packageInfo,
          ...(await VersionInfoService.generateVersionInfo(options)),
        }

        logger.info('\n📦 版本信息:')
        logger.info(`  项目名称: ${versionInfo.name ?? '未知'}`)
        logger.info(`  版本号: ${versionInfo.version ?? '未知'}`)
        logger.info(`  构建时间: ${versionInfo.buildTime}`)
        logger.info(`  Git 分支: ${versionInfo.gitBranch}`)
        logger.info(`  Git 提交: ${versionInfo.gitCommitShort}`)
        logger.info(`  Git 标签: ${versionInfo.gitTag}`)
        logger.info('')
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        logger.error(`⚠️ 版本信息生成失败: ${errorMessage}`)
        versionInfo = {}
      }
    },

    resolveId(id) {
      if (id === virtualModuleId) {
        return resolvedVirtualModuleId
      }
      return null
    },

    load(id) {
      if (id === resolvedVirtualModuleId) {
        // 生成虚拟模块代码
        return `
        // 版本信息虚拟模块
        // 由 @tcsl/quality-helper 自动生成

        export const versionInfo = ${JSON.stringify(versionInfo, null, 2)}

        // 导出各个字段以便按需使用
        export const name = ${JSON.stringify(versionInfo.name)}
        export const version = ${JSON.stringify(versionInfo.version)}
        export const buildTime = ${JSON.stringify(versionInfo.buildTime)}
        export const buildTimestamp = ${JSON.stringify(versionInfo.buildTimestamp)}
        export const gitCommitHash = ${JSON.stringify(versionInfo.gitCommitHash)}
        export const gitCommitShort = ${JSON.stringify(versionInfo.gitCommitShort)}
        export const gitBranch = ${JSON.stringify(versionInfo.gitBranch)}
        export const gitTag = ${JSON.stringify(versionInfo.gitTag)}
        export const gitCommitCount = ${JSON.stringify(versionInfo.gitCommitCount)}
        export const gitCommitDate = ${JSON.stringify(versionInfo.gitCommitDate)}
        export const gitCommitMessage = ${JSON.stringify(versionInfo.gitCommitMessage)}
        export const gitAuthor = ${JSON.stringify(versionInfo.gitAuthor)}
        export const gitEmail = ${JSON.stringify(versionInfo.gitEmail)}
        export const gitDirty = ${JSON.stringify(versionInfo.gitDirty)}

        // 默认导出完整的版本信息
        export default versionInfo
        `
      }
      return null
    },
  }
}
